import { useState, useEffect } from 'react';
import { Field, Callout, LoadRow } from '../UI';
import { fmtMoney } from '../../utils/constants';

export default function BidEvalTab({ contract, updateC, apiKey, callAI, toast, profile, s215, assignedCm }) {
  const eval_ = contract.bidEval || {};
  const upd = (k, v) => updateC({ bidEval: { ...eval_, [k]: v } });

  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  const criteria = eval_.criteria || [];
  const bidders = eval_.bidders || [];
  const scores = eval_.scores || {};

  // Build the full contractor display name including DBA if present.
  // Used for auto-populating the winning bidder and display throughout.
  function contractorDisplayName() {
    const name = s215?.contractorName || '';
    const dba = s215?.contractorDba || '';
    if (!name) return '';
    return dba ? name + ' dba ' + dba : name;
  }

  // Auto-populate the awarded contractor as a bidder if:
  //   1. s215.contractorName is filled (a vendor has been selected)
  //   2. No existing bidder already matches that name
  // This runs once on mount and when s215 changes.
  useEffect(() => {
    const displayName = contractorDisplayName();
    if (!displayName) return;
    const alreadyExists = bidders.some(b =>
      b.name?.toLowerCase().trim() === displayName.toLowerCase().trim()
      || b.name?.toLowerCase().trim() === (s215?.contractorName || '').toLowerCase().trim()
    );
    if (!alreadyExists && bidders.length === 0) {
      // Auto-add the awarded contractor as the first bidder
      upd('bidders', [
        { id: Date.now(), name: displayName, certifications: '', awarded: true },
        ...bidders,
      ]);
    }
  }, [s215?.contractorName, s215?.contractorDba]); // eslint-disable-line

  function addCriterion() {
    upd('criteria', [...criteria, { id: Date.now(), name: '', weight: 10, maxScore: 100 }]);
  }
  function updateCriterion(id, k, v) {
    upd('criteria', criteria.map(c => c.id === id ? { ...c, [k]: v } : c));
  }
  function removeCriterion(id) {
    upd('criteria', criteria.filter(c => c.id !== id));
  }

  function addBidder() {
    upd('bidders', [...bidders, { id: Date.now(), name: '', certifications: '' }]);
  }
  function updateBidder(id, k, v) {
    upd('bidders', bidders.map(b => b.id === id ? { ...b, [k]: v } : b));
  }
  function removeBidder(id) {
    upd('bidders', bidders.filter(b => b.id !== id));
  }

  function setScore(bidderId, criterionId, value) {
    const key = bidderId + '_' + criterionId;
    upd('scores', { ...scores, [key]: Number(value) });
  }
  function getScore(bidderId, criterionId) {
    return scores[bidderId + '_' + criterionId] || 0;
  }

  // Weighted score for a bidder
  function weightedScore(bidderId) {
    if (criteria.length === 0) return 0;
    const totalWeight = criteria.reduce((s, c) => s + (Number(c.weight) || 0), 0);
    if (totalWeight === 0) return 0;
    return criteria.reduce((sum, c) => {
      const raw = getScore(bidderId, c.id);
      const max = Number(c.maxScore) || 100;
      const weight = Number(c.weight) || 0;
      return sum + (raw / max) * weight;
    }, 0) / totalWeight * 100;
  }

  // Rank bidders
  const ranked = [...bidders].sort((a, b) => weightedScore(b.id) - weightedScore(a.id));

  async function draftCriteria() {
    setLoading(true);
    try {
      const sys = 'You are Penny, a California state contracting expert. Based on this contract and procurement method, suggest evaluation criteria for the bid evaluation matrix. Return ONLY a valid JSON array like this: [{"name":"Technical Approach","weight":30,"maxScore":100,"description":"Quality of proposed methodology"},{"name":"Qualifications","weight":25,"maxScore":100,"description":"Relevant experience and certifications"}]. Suggest 5-7 criteria. Weights must sum to 100. No other text.';
      const text = await callAI(apiKey, {
        system: sys, model: 'claude-sonnet-4-6', max_tokens: 500,
        messages: [{ role: 'user', content: 'Contract: ' + contract.title + '\nMethod: ' + (contract.procMethod || 'RFP') + '\nCategory: ' + (contract.category || 'Professional Services') + '\nDescription: ' + (contract.description || '') }],
      });
      const clean = text.replace(/```json|```/g, '').trim();
      const parsed = JSON.parse(clean);
      upd('criteria', parsed.map((c, i) => ({ id: Date.now() + i, ...c })));
      toast.success(parsed.length + ' evaluation criteria drafted by Penny');
    } catch (e) { toast.error('Draft failed: ' + e.message); }
    finally { setLoading(false); }
  }

  function exportEvalMatrix() {
    setExportLoading(true);
    try {
      const lines = [
        'BID EVALUATION MATRIX',
        'Contract: ' + (contract.title || 'TBD'),
        'Procurement Method: ' + (contract.procMethod || 'TBD'),
        'Date: ' + new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
        '',
        '1. EVALUATION CRITERIA AND WEIGHTS',
        '',
        ...criteria.map((c, i) => (i+1) + '. ' + c.name + ' — Weight: ' + c.weight + '% (Max Score: ' + c.maxScore + ')' + (c.description ? '\n   ' + c.description : '')),
        '',
        '2. BIDDER SCORES',
        '',
        ...ranked.map((b, i) => {
          const ws = weightedScore(b.id);
          const lines = ['Rank ' + (i+1) + ': ' + (b.name || 'Bidder') + (b.certifications ? ' (' + b.certifications + ')' : '')];
          criteria.forEach(c => {
            const score = getScore(b.id, c.id);
            lines.push('   ' + c.name + ': ' + score + '/' + c.maxScore);
          });
          lines.push('   WEIGHTED TOTAL: ' + ws.toFixed(1) + '/100');
          return lines.join('\n');
        }),
        '',
        '3. RANKING SUMMARY',
        '',
        ...ranked.map((b, i) => 'Rank ' + (i+1) + ': ' + (b.name || 'Bidder') + ' — ' + weightedScore(b.id).toFixed(1) + '/100'),
        '',
        '4. EVALUATOR CERTIFICATION',
        '',
        'I certify that this evaluation was conducted in accordance with the evaluation criteria',
        'stated in the solicitation document and in compliance with SCM Vol 1, Chapter 5.',
        '',
        'Evaluator Signature: _______________________________  Date: ___________',
        'Printed Name: _____________________________________  Title: ___________',
      ];
      const text = lines.join('\n');
      const blob = new Blob([text], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = ('Bid_Evaluation_Matrix_' + (contract.title || 'Contract')).replace(/[^a-zA-Z0-9_-]/g, '_').slice(0, 50) + '.txt';
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Bid evaluation matrix exported');
    } catch (e) { toast.error('Export failed: ' + e.message); }
    finally { setExportLoading(false); }
  }

  const totalWeight = criteria.reduce((s, c) => s + (Number(c.weight) || 0), 0);

  return (
    <div>
      {/* Criteria setup */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
          <div>
            <h3 className="card-title">✦ Evaluation Criteria · Penny</h3>
            <p style={{ fontSize: 12.5, color: 'var(--slate-500)' }}>
              Define weighted scoring criteria. Penny can draft them from your solicitation.
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-gold btn-sm" disabled={loading} onClick={draftCriteria}>
              {loading ? <><div className="spin spin-sm" /> Drafting…</> : '✦ Draft with Penny'}
            </button>
            <button className="btn btn-ghost btn-sm" onClick={addCriterion}>+ Add Criterion</button>
          </div>
        </div>

        {loading && <LoadRow msg="Penny is drafting evaluation criteria from your solicitation…" />}

        {criteria.length === 0 && !loading && (
          <div style={{ fontSize: 13, color: 'var(--slate-400)', fontStyle: 'italic', padding: '16px 0' }}>
            No criteria yet — click "Draft with Penny" or add manually.
          </div>
        )}

        {criteria.length > 0 && (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
              {criteria.map((c, i) => (
                <div key={c.id} style={{ display: 'grid', gridTemplateColumns: '1fr 80px 80px auto', gap: 8, alignItems: 'center', padding: '10px 12px', background: 'var(--slate-50)', borderRadius: 'var(--r-md)', border: '1px solid var(--slate-200)' }}>
                  <div>
                    <input
                      value={c.name}
                      onChange={e => updateCriterion(c.id, 'name', e.target.value)}
                      placeholder={'Criterion ' + (i+1) + ' name…'}
                      style={{ width: '100%', padding: '6px 8px', borderRadius: 'var(--r-md)', border: '1px solid var(--slate-200)', fontSize: 13 }}
                    />
                    {c.description && <div style={{ fontSize: 11, color: 'var(--slate-400)', marginTop: 3 }}>{c.description}</div>}
                  </div>
                  <div>
                    <label style={{ fontSize: 10, color: 'var(--slate-400)', display: 'block', marginBottom: 2 }}>Weight %</label>
                    <input
                      type="number" min="0" max="100"
                      value={c.weight}
                      onChange={e => updateCriterion(c.id, 'weight', e.target.value)}
                      style={{ width: '100%', padding: '6px 8px', borderRadius: 'var(--r-md)', border: '1px solid var(--slate-200)', fontSize: 13 }}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: 10, color: 'var(--slate-400)', display: 'block', marginBottom: 2 }}>Max Score</label>
                    <input
                      type="number" min="1"
                      value={c.maxScore}
                      onChange={e => updateCriterion(c.id, 'maxScore', e.target.value)}
                      style={{ width: '100%', padding: '6px 8px', borderRadius: 'var(--r-md)', border: '1px solid var(--slate-200)', fontSize: 13 }}
                    />
                  </div>
                  <button className="btn btn-ghost btn-xs" style={{ color: 'var(--red-400)' }} onClick={() => removeCriterion(c.id)}>✕</button>
                </div>
              ))}
            </div>
            <div style={{ fontSize: 12, color: totalWeight === 100 ? 'var(--green-600)' : 'var(--red-600)', fontWeight: 600 }}>
              {totalWeight === 100 ? '✓ Weights sum to 100%' : '⚠ Weights sum to ' + totalWeight + '% — must equal 100%'}
            </div>
          </>
        )}
      </div>

      {/* Bidders */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 className="card-title">Bidders / Proposers</h3>
          <button className="btn btn-ghost btn-sm" onClick={addBidder}>+ Add Bidder</button>
        </div>

        {bidders.length === 0 ? (
          <div style={{ fontSize: 13, color: 'var(--slate-400)', fontStyle: 'italic' }}>No bidders yet — add them as proposals come in.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {bidders.map(b => (
              <div key={b.id} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 8, alignItems: 'center' }}>
                <input
                  value={b.name}
                  onChange={e => updateBidder(b.id, 'name', e.target.value)}
                  placeholder="Company / Bidder name…"
                  style={{ padding: '8px 10px', borderRadius: 'var(--r-md)', border: '1.5px solid var(--slate-200)', fontSize: 13 }}
                />
                <input
                  value={b.certifications}
                  onChange={e => updateBidder(b.id, 'certifications', e.target.value)}
                  placeholder="SB, DVBE, certifications…"
                  style={{ padding: '8px 10px', borderRadius: 'var(--r-md)', border: '1.5px solid var(--slate-200)', fontSize: 13 }}
                />
                <button className="btn btn-ghost btn-xs" style={{ color: 'var(--red-400)' }} onClick={() => removeBidder(b.id)}>✕</button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Scoring matrix */}
      {criteria.length > 0 && bidders.length > 0 && (
        <div className="card">
          <h3 className="card-title" style={{ marginBottom: 16 }}>Scoring Matrix</h3>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: '8px 12px', borderBottom: '2px solid var(--slate-200)', color: 'var(--slate-500)', fontWeight: 600, minWidth: 140 }}>Bidder</th>
                  {criteria.map(c => (
                    <th key={c.id} style={{ textAlign: 'center', padding: '8px 10px', borderBottom: '2px solid var(--slate-200)', color: 'var(--slate-500)', fontWeight: 600, minWidth: 100 }}>
                      <div>{c.name}</div>
                      <div style={{ fontSize: 10, fontWeight: 400 }}>{c.weight}% · max {c.maxScore}</div>
                    </th>
                  ))}
                  <th style={{ textAlign: 'center', padding: '8px 12px', borderBottom: '2px solid var(--slate-200)', color: 'var(--navy-700)', fontWeight: 700, minWidth: 80 }}>Weighted</th>
                  <th style={{ textAlign: 'center', padding: '8px 12px', borderBottom: '2px solid var(--slate-200)', color: 'var(--navy-700)', fontWeight: 700, minWidth: 60 }}>Rank</th>
                </tr>
              </thead>
              <tbody>
                {ranked.map((b, rank) => (
                  <tr key={b.id} style={{ background: rank === 0 ? 'var(--teal-50)' : '' }}>
                    <td style={{ padding: '8px 12px', borderBottom: '1px solid var(--slate-100)', fontWeight: 600 }}>
                      {b.name || 'Bidder'}
                      {b.certifications && <div style={{ fontSize: 11, color: 'var(--teal-600)', fontWeight: 400 }}>{b.certifications}</div>}
                    </td>
                    {criteria.map(c => (
                      <td key={c.id} style={{ padding: '6px 10px', borderBottom: '1px solid var(--slate-100)', textAlign: 'center' }}>
                        <input
                          type="number" min="0" max={c.maxScore}
                          value={getScore(b.id, c.id)}
                          onChange={e => setScore(b.id, c.id, e.target.value)}
                          style={{ width: 70, padding: '4px 6px', textAlign: 'center', borderRadius: 6, border: '1px solid var(--slate-200)', fontSize: 13 }}
                        />
                      </td>
                    ))}
                    <td style={{ padding: '8px 12px', borderBottom: '1px solid var(--slate-100)', textAlign: 'center', fontWeight: 700, color: rank === 0 ? 'var(--teal-700)' : 'var(--navy-700)' }}>
                      {weightedScore(b.id).toFixed(1)}
                    </td>
                    <td style={{ padding: '8px 12px', borderBottom: '1px solid var(--slate-100)', textAlign: 'center', fontWeight: 700, color: rank === 0 ? 'var(--teal-700)' : 'var(--slate-500)' }}>
                      {rank === 0 ? '🥇' : rank === 1 ? '🥈' : rank === 2 ? '🥉' : rank + 1}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {ranked.length > 0 && (
            <div style={{ marginTop: 16, padding: '12px 16px', background: 'var(--teal-50)', border: '1.5px solid var(--teal-200)', borderRadius: 'var(--r-md)' }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--teal-700)' }}>
                ✓ Recommended Award: {ranked[0]?.name || 'Bidder 1'} — {weightedScore(ranked[0]?.id).toFixed(1)}/100
              </div>
              <div style={{ fontSize: 12, color: 'var(--teal-600)', marginTop: 4 }}>
                Document selection rationale in writing before issuing Intent to Award notice.
              </div>
            </div>
          )}

          <div style={{ marginTop: 16 }}>
            <button className="btn btn-gold" disabled={exportLoading || totalWeight !== 100} onClick={exportEvalMatrix}>
              {exportLoading ? <><div className="spin spin-sm" /> Exporting…</> : '↓ Export Evaluation Matrix (.docx)'}
            </button>
            {totalWeight !== 100 && <span style={{ fontSize: 11.5, color: 'var(--red-500)', marginLeft: 10 }}>Weights must sum to 100% before exporting</span>}
          </div>
        </div>
      )}
    </div>
  );
}
